import React, { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Collapse } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import {
  ExpandLessRounded as UpIcon,
  ExpandMoreRounded as DownIcon,
  FileCopyRounded as FileIcon,
  FindInPageRounded as InfoIcon,
} from '@material-ui/icons'

import DateTime from 'luxon/src/datetime.js'

import { objLength } from '../../../utils/customMethods'

import EmptyState from '../../atoms/display/emptyState'
import IconButton from '../../atoms/inputs/iconButton'
import Typography from '../../atoms/display/typography'
import EscapeDrag from '../../molecules/display/escapeDrag'

import useStyles from './ExtraInformationStyle'

const ExtraInformation = (props) => {
  const classes = useStyles()

  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const generalInformation = useSelector((state) => state.generalInformation)
  const [expanded, setExpanded] = useState({
    0: false,
  })

  const handleCollapse = useCallback((index) => {
    setExpanded((expanded) => ({
      ...expanded,
      [index]: !expanded[index],
    }))
  }, [])

  const copyToClippboard = (string) => {
    navigator.clipboard.writeText(string).then(
      () => {
        enqueueSnackbar(`${t('commons.copied')}`, {
          variant: 'success',
        })
      },
      (err) => {
        console.error(t('commons.copiedError'), err)
      }
    )
  }

  // check if there's this translation in local files. Otherwise, return original text (got from api)
  const translateKey = (path, key) => {
    const translate = t(`${path}.${key}`)
    return !translate?.includes('.') ? translate : key
  }

  const checkValueType = (value) => {
    let type = null
    if (value) {
      switch (Object.prototype.toString.call(value)) {
        case '[object Boolean]':
          type = 'boolean'
          break
        case '[object Number]':
          type = 'number'
          break
        case '[object String]':
          if (Date.parse(value)) {
            type = 'date'
          } else {
            type = 'string'
          }
          break
        case '[object Date]':
          type = 'date'
          break
        case '[object Array]':
          type = 'array'
          break
        case '[object Object]':
          type = 'object'
          break
        default:
          type = null
          break
      }
    }
    return type
  }

  // TODO: implement this function when it'll be used (decide which is better: a checkbox or text)
  const renderBollean = (key, value) => value

  const renderString = (key, value) => value

  const renderDate = (key, value) => {
    const date = new Date(value)
    const format = `D '-' t`
    const time = DateTime.fromJSDate(date).setLocale('pt-br').toFormat(format)

    return isNaN(date.getTime()) ? value : time
  }

  // TODO: implement this function when it'll be used
  const renderArray = (key, value) => null

  // TODO: implement this function when it'll be used
  const renderObject = (key, value) => null

  const renderExtraInfo = (key, value, type) => {
    let renderInfo = null
    if (type) {
      switch (type) {
        case 'boolean':
          renderInfo = renderBollean(key, value)
          break
        case 'number':
          renderInfo = renderString(key, value)
          break
        case 'string':
          renderInfo = renderString(key, value)
          break
        case 'date':
          renderInfo = renderDate(key, value)
          break
        case 'array':
          renderInfo = renderArray(key, value)
          break
        case 'object':
          renderInfo = renderObject(key, value)
          break
        default:
          renderInfo = null
          break
      }
    }
    return renderInfo
  }

  const renderCopyItem = (value) => (
    <div className="copyIcon">
      <IconButton
        color="primary"
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          copyToClippboard(value)
        }}
      >
        <FileIcon />
      </IconButton>
    </div>
  )

  const renderItem = (key, value, enableCopy, translatePath = 'commons') => (
    <tr>
      <td>
        <Typography variant="body1">
          {translateKey(translatePath, key)}
        </Typography>
      </td>
      <td>
        <div className="subcontent">
          <Typography variant="body1">
            <EscapeDrag>{value}</EscapeDrag>
          </Typography>
          {enableCopy && renderCopyItem(value)}
        </div>
      </td>
    </tr>
  )

  const renderBody = () => {
    const chatId = props?.chatId
    const data = generalInformation.conversations[chatId]?.extraInfo
    const copyAllowed = [
      'skill_id',
      'live_person_id',
      'altu_identifier',
      'webChatConversationId',
    ]

    return objLength(data) > 0 ? (
      Object.entries(data).map((el) => {
        const valueType = checkValueType(el[1])
        const value = renderExtraInfo(el[0], el[1], valueType) ?? '-'

        return copyAllowed.includes(el[0])
          ? renderItem(el[0], value, true)
          : renderItem(el[0], value)
      })
    ) : (
      <div className={classes.emptySpace}>
        <EmptyState text={t('items.notfound')}>
          <InfoIcon />
        </EmptyState>
      </div>
    )
  }

  const renderContent = () => {
    return (
      <>
        <div className={classes.menuBar} onClick={() => handleCollapse(0)}>
          <Typography variant="body1">
            {t('commons.extraInformation')}
          </Typography>
          <div className={classes.arrowContent}>
            {expanded[0] ? <UpIcon /> : <DownIcon />}
          </div>
        </div>
        <Collapse in={expanded[0]} timeout="auto" unmountOnExit>
          <div className={classes.content}>
            <table width="100%" cellPadding="10">
              <tbody>{renderBody()}</tbody>
            </table>
          </div>
        </Collapse>
      </>
    )
  }

  return renderContent()
}

export default ExtraInformation
